import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Edit2, X, GripVertical, Clock, MessageSquare, Send, UserCheck, ChevronDown, ChevronUp, FolderOpen, CheckCircle, Circle, TrendingUp, FileText, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Client task templates for reserve recovery
const CLIENT_TASK_TEMPLATES = [
  { title: 'Upload Processing Statements', description: 'Upload your last 6 months of merchant processing statements' },
  { title: 'Provide Bank Documentation', description: 'Upload bank statements showing held reserves' },
  { title: 'Complete Identity Verification', description: 'Submit ID documents for verification' },
  { title: 'Sign Limited POA', description: 'Review and sign the Limited Power of Attorney document' },
  { title: 'Review Processor Agreement', description: 'Provide copy of your merchant processing agreement' },
  { title: 'Submit Chargeback Documentation', description: 'Provide any chargeback-related correspondence' },
];

const COLUMNS = {
  todo: { id: 'todo', title: 'To Do', color: 'bg-slate-100', borderColor: 'border-slate-300', textColor: 'text-slate-700', icon: Circle },
  in_progress: { id: 'in_progress', title: 'In Progress', color: 'bg-blue-50', borderColor: 'border-blue-300', textColor: 'text-blue-700', icon: Clock },
  done: { id: 'done', title: 'Done', color: 'bg-green-50', borderColor: 'border-green-300', textColor: 'text-green-700', icon: CheckCircle }
};

const ClientKanbanBoard = ({ cases = [] }) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [caseTasks, setCaseTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTaskDetail, setShowTaskDetail] = useState(null);
  const [newTask, setNewTask] = useState({ title: '', description: '', status: 'todo', case_id: '' });
  const [newComment, setNewComment] = useState('');
  const [expandedTasks, setExpandedTasks] = useState({});
  const [viewMode, setViewMode] = useState('cases'); // 'cases' or 'kanban'
  const [selectedCase, setSelectedCase] = useState(null);
  const [expandedCases, setExpandedCases] = useState({});

  useEffect(() => {
    loadAllTasks();
    loadCaseTasks();
  }, [cases]);

  const loadAllTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load case-specific tasks for each case
  const loadCaseTasks = async () => {
    const tasksByCase = {};
    for (const caseItem of cases) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/case-tasks/${caseItem.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          tasksByCase[caseItem.id] = data;
        }
      } catch (error) {
        console.error(`Failed to load tasks for case ${caseItem.id}:`, error);
      }
    }
    setCaseTasks(tasksByCase);
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const task = tasks.find(t => t.id === draggableId);
    if (!task) return;

    const newStatus = destination.droppableId;
    const oldStatus = source.droppableId;

    // Optimistically update UI
    setTasks(prevTasks => 
      prevTasks.map(t => t.id === draggableId ? { ...t, status: newStatus } : t)
    );

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/client/tasks/${draggableId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Task moved to ${COLUMNS[newStatus]?.title}`);
    } catch (error) {
      setTasks(prevTasks => 
        prevTasks.map(t => t.id === draggableId ? { ...t, status: oldStatus } : t)
      );
      toast.error('Failed to update task status');
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/tasks`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newTask.title,
          description: newTask.description || '',
          status: newTask.status,
          case_id: newTask.case_id || (cases[0]?.id || null)
        })
      });

      if (response.ok) {
        const createdTask = await response.json();
        setTasks([...tasks, createdTask]);
        toast.success('Task created successfully');
        setShowAddModal(false);
        setNewTask({ title: '', description: '', status: 'todo', case_id: '' });
      }
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleAddComment = async (taskId) => {
    if (!newComment.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/tasks/${taskId}/comments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newComment })
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
        setNewComment('');
        toast.success('Comment added');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleRequestAdminReview = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/tasks/${taskId}/request-review`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
        toast.success('Admin review requested! The team will review your progress.');
      }
    } catch (error) {
      toast.error('Failed to request review');
    }
  };

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const toggleCaseExpand = (caseId) => {
    setExpandedCases(prev => ({ ...prev, [caseId]: !prev[caseId] }));
  };

  const getTasksByStatus = (status) => tasks.filter(task => task.status === status);

  const getCaseName = (caseId) => {
    const caseItem = cases.find(c => c.id === caseId);
    return caseItem?.company_name || 'General';
  };

  // Calculate case progress
  const getCaseProgress = (caseId) => {
    const cTasks = caseTasks[caseId] || [];
    if (cTasks.length === 0) return { total: 0, done: 0, percentage: 0 };
    const done = cTasks.filter(t => t.status === 'done').length;
    return {
      total: cTasks.length,
      done,
      inProgress: cTasks.filter(t => t.status === 'in_progress').length,
      todo: cTasks.filter(t => t.status === 'todo').length,
      percentage: Math.round((done / cTasks.length) * 100)
    };
  };

  // Get status counts for all cases
  const getStatusCounts = () => {
    const counts = { todo: [], in_progress: [], done: [] };
    cases.forEach(c => {
      const cTasks = caseTasks[c.id] || [];
      cTasks.forEach(t => {
        if (counts[t.status]) {
          counts[t.status].push({ caseId: c.id, caseName: c.company_name, taskTitle: t.title });
        }
      });
    });
    return counts;
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading tasks...</div>;
  }

  const statusCounts = getStatusCounts();

  return (
    <div className="space-y-6">
      {/* Header with View Toggle */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Your Task Board</h3>
          <p className="text-sm text-slate-500">Track progress across all your cases</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('cases')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'cases' 
                  ? 'bg-white text-purple-700 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <FolderOpen size={16} className="inline mr-2" />
              By Case
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'kanban' 
                  ? 'bg-white text-purple-700 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <TrendingUp size={16} className="inline mr-2" />
              Kanban
            </button>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="primary-button flex items-center gap-2"
          >
            <Plus size={18} />
            Add Task
          </button>
        </div>
      </div>

      {/* Status Overview Cards */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(COLUMNS).map(([status, column]) => {
          const Icon = column.icon;
          const count = statusCounts[status]?.length || 0;
          return (
            <div
              key={status}
              className={`${column.color} border-2 ${column.borderColor} rounded-xl p-4 cursor-pointer hover:shadow-md transition-all`}
              onClick={() => setViewMode('kanban')}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon size={20} className={column.textColor} />
                  <span className={`font-semibold ${column.textColor}`}>{column.title}</span>
                </div>
                <span className={`text-2xl font-bold ${column.textColor}`}>{count}</span>
              </div>
              <p className="text-xs text-slate-500">
                {count === 1 ? '1 task' : `${count} tasks`} across {new Set(statusCounts[status]?.map(t => t.caseId)).size} cases
              </p>
            </div>
          );
        })}
      </div>

      {/* Cases View */}
      {viewMode === 'cases' && (
        <div className="space-y-4">
          <h4 className="font-semibold text-slate-900">Cases & Progress</h4>
          
          {cases.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <FolderOpen className="mx-auto text-slate-400 mb-4" size={48} />
              <p className="text-slate-600">No cases found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cases.map((caseItem) => {
                const progress = getCaseProgress(caseItem.id);
                const cTasks = caseTasks[caseItem.id] || [];
                const isExpanded = expandedCases[caseItem.id];
                
                return (
                  <div key={caseItem.id} className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden hover:border-purple-300 transition-all">
                    {/* Case Header */}
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => toggleCaseExpand(caseItem.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            progress.percentage === 100 ? 'bg-green-100' : 
                            progress.percentage > 0 ? 'bg-blue-100' : 'bg-slate-100'
                          }`}>
                            <FileText size={20} className={
                              progress.percentage === 100 ? 'text-green-600' : 
                              progress.percentage > 0 ? 'text-blue-600' : 'text-slate-600'
                            } />
                          </div>
                          <div>
                            <h5 className="font-semibold text-slate-900">{caseItem.company_name}</h5>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <span>ID: {caseItem.id.slice(0, 8)}...</span>
                              <span>•</span>
                              <span className={`px-2 py-0.5 rounded-full ${
                                caseItem.status === 'resolved' ? 'bg-green-100 text-green-700' :
                                caseItem.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {caseItem.status.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {/* Progress Bar */}
                          <div className="w-32 hidden sm:block">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                              <span>{progress.done}/{progress.total} tasks</span>
                              <span>{progress.percentage}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full transition-all ${
                                  progress.percentage === 100 ? 'bg-green-500' : 'bg-purple-500'
                                }`}
                                style={{ width: `${progress.percentage}%` }}
                              />
                            </div>
                          </div>
                          
                          {/* Task Status Summary */}
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                              {progress.todo} todo
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
                              {progress.inProgress} active
                            </span>
                            <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full">
                              {progress.done} done
                            </span>
                          </div>
                          
                          <ChevronDown 
                            size={20} 
                            className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Task List */}
                    {isExpanded && (
                      <div className="border-t border-slate-200 bg-slate-50 p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h6 className="font-semibold text-slate-700">Case Tasks</h6>
                          <Link 
                            to={`/case/${caseItem.id}`}
                            className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
                          >
                            View Full Case <ExternalLink size={14} />
                          </Link>
                        </div>
                        
                        {cTasks.length === 0 ? (
                          <p className="text-sm text-slate-500 text-center py-4">No tasks for this case</p>
                        ) : (
                          <div className="space-y-2">
                            {cTasks.sort((a, b) => (a.order || 99) - (b.order || 99)).map((task, idx) => (
                              <div 
                                key={task.id} 
                                className={`flex items-center gap-3 p-3 rounded-lg border ${
                                  task.status === 'done' ? 'bg-green-50 border-green-200' :
                                  task.status === 'in_progress' ? 'bg-blue-50 border-blue-200' :
                                  'bg-white border-slate-200'
                                }`}
                              >
                                <span className="text-xs text-slate-400 w-6">{idx + 1}.</span>
                                {task.status === 'done' ? (
                                  <CheckCircle size={18} className="text-green-500" />
                                ) : task.status === 'in_progress' ? (
                                  <Clock size={18} className="text-blue-500" />
                                ) : (
                                  <Circle size={18} className="text-slate-400" />
                                )}
                                <div className="flex-1">
                                  <span className={`text-sm ${task.status === 'done' ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                                    {task.title}
                                  </span>
                                  {task.description && (
                                    <p className="text-xs text-slate-500 mt-0.5">{task.description}</p>
                                  )}
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  task.status === 'done' ? 'bg-green-100 text-green-700' :
                                  task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {task.status === 'done' ? 'Completed' : 
                                   task.status === 'in_progress' ? 'In Progress' : 'To Do'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Kanban Board View */}
      {viewMode === 'kanban' && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.values(COLUMNS).map((column) => (
              <Droppable key={column.id} droppableId={column.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`rounded-xl p-4 min-h-[450px] border-2 transition-all ${column.color} ${
                      snapshot.isDraggingOver ? 'border-purple-400 bg-purple-50' : column.borderColor
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className={`font-bold text-lg ${column.textColor}`}>
                        {column.title}
                      </h4>
                      <span className={`text-sm font-semibold px-3 py-1 rounded-full ${column.color} ${column.textColor} border ${column.borderColor}`}>
                        {getTasksByStatus(column.id).length}
                      </span>
                    </div>

                    <div className="space-y-3">
                      {getTasksByStatus(column.id).map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`bg-white rounded-lg shadow-sm border-2 border-slate-200 overflow-hidden ${
                                snapshot.isDragging ? 'shadow-xl ring-2 ring-purple-400 border-purple-400' : 'hover:border-purple-300'
                              }`}
                            >
                              {/* Task Header */}
                              <div className="p-4">
                                <div className="flex items-start gap-3">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="text-slate-400 hover:text-purple-600 cursor-grab mt-1"
                                  >
                                    <GripVertical size={18} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h5 className="font-semibold text-slate-900">{task.title}</h5>
                                    {task.case_id && (
                                      <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                                        {getCaseName(task.case_id)}
                                      </span>
                                    )}
                                    {task.description && (
                                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">{task.description}</p>
                                    )}
                                    
                                    {/* Task Meta */}
                                    <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                                      <span className="flex items-center gap-1">
                                        <Clock size={12} />
                                        {task.updated_at ? format(new Date(task.updated_at), 'MMM d') : 'N/A'}
                                      </span>
                                      {task.comments?.length > 0 && (
                                        <span className="flex items-center gap-1">
                                          <MessageSquare size={12} />
                                          {task.comments.length}
                                        </span>
                                      )}
                                      {task.admin_review_requested && (
                                        <span className="flex items-center gap-1 text-amber-600">
                                          <UserCheck size={12} />
                                          Review Requested
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => toggleTaskExpand(task.id)}
                                    className="p-1 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                                  >
                                    {expandedTasks[task.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                  </button>
                                </div>
                              </div>

                              {/* Expanded Section */}
                              {expandedTasks[task.id] && (
                                <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-4">
                                  {/* Comments */}
                                  <div>
                                    <h6 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                                      <MessageSquare size={14} />
                                      Comments ({task.comments?.length || 0})
                                    </h6>
                                    
                                    {task.comments?.length > 0 && (
                                      <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                                        {task.comments.map((comment, idx) => (
                                          <div key={idx} className="bg-white rounded p-2 text-sm border border-slate-200">
                                            <div className="flex justify-between items-start">
                                              <span className="font-medium text-slate-800">{comment.author_name}</span>
                                              <span className="text-xs text-slate-400">
                                                {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                                              </span>
                                            </div>
                                            <p className="text-slate-600 mt-1">{comment.content}</p>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    
                                    {/* Add Comment */}
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        value={showTaskDetail === task.id ? newComment : ''}
                                        onChange={(e) => {
                                          setShowTaskDetail(task.id);
                                          setNewComment(e.target.value);
                                        }}
                                        onFocus={() => setShowTaskDetail(task.id)}
                                        placeholder="Add a comment..."
                                        className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                      />
                                      <button
                                        onClick={() => handleAddComment(task.id)}
                                        className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                                      >
                                        <Send size={16} />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Request Admin Review */}
                                  {!task.admin_review_requested && task.status !== 'done' && (
                                    <button
                                      onClick={() => handleRequestAdminReview(task.id)}
                                      className="w-full py-2 border-2 border-dashed border-amber-300 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-50 flex items-center justify-center gap-2"
                                    >
                                      <UserCheck size={16} />
                                      Request Admin Review
                                    </button>
                                  )}
                                  {task.admin_review_requested && (
                                    <div className="py-2 px-3 bg-amber-100 text-amber-800 rounded-lg text-sm flex items-center gap-2">
                                      <UserCheck size={16} />
                                      Admin review requested - awaiting feedback
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      
                      {getTasksByStatus(column.id).length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                          <p>No tasks in {column.title.toLowerCase()}</p>
                          <p className="text-xs mt-1">Drag tasks here</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        </DragDropContext>
      )}

      {/* Add Task Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-slate-900">Add New Task</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleAddTask} className="p-6 space-y-5">
              {/* Case Selection */}
              {cases.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Related Case</label>
                  <select
                    value={newTask.case_id}
                    onChange={(e) => setNewTask({ ...newTask, case_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  >
                    <option value="">Select a case (optional)</option>
                    {cases.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name} (ID: {c.id.slice(0, 8)}...)</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Task Title *</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="What needs to be done?"
                  required
                />
              </div>

              {/* Quick Templates */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Quick Templates</label>
                <div className="flex flex-wrap gap-2">
                  {CLIENT_TASK_TEMPLATES.map((template, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setNewTask({ ...newTask, title: template.title, description: template.description })}
                      className="text-xs px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full hover:bg-purple-100 hover:text-purple-700 transition-colors"
                    >
                      {template.title}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
                  rows={3}
                  placeholder="Add more details..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Initial Status</label>
                <div className="flex gap-2">
                  {Object.values(COLUMNS).map((col) => (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() => setNewTask({ ...newTask, status: col.id })}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                        newTask.status === col.id
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : `${col.color} ${col.textColor} border-transparent hover:border-slate-300`
                      }`}
                    >
                      {col.title}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 primary-button py-3">
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientKanbanBoard;
